import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  TextField,
  InputAdornment,
  Skeleton,
  Paper,
  Divider
} from '@mui/material';
import {
  Download as DownloadIcon,
  AdminPanelSettings as AdminIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import api from '../services/api';

const AdminViewer = () => {
  const [adminData, setAdminData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [exportingAdmins, setExportingAdmins] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Test admin data fetch (simplified)
  const testAdminData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🧪 Testing admin data fetch...');
      const response = await api.get('/api/profiles/admins?test=true');
      
      if (response.data.success) {
        console.log('✅ Test successful:', response.data.testData);
        setSuccessMessage('Test successful! Check console for details.');
        setTimeout(() => setSuccessMessage(null), 5000);
      } else {
        setError('Test failed: ' + (response.data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('❌ Error testing admin data:', error);
      setError('Test failed: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch admin data
  const fetchAdminData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔍 Fetching admin data...');
      const response = await api.get('/api/profiles/admins');
      
      if (response.data.success) {
        setAdminData(response.data.data || []);
        console.log(`✅ Loaded ${response.data.data?.length || 0} locations with admin data`);
        
        if (response.data.errors && response.data.errors.length > 0) {
          console.warn('⚠️ Some locations had errors:', response.data.errors);
        }
      } else {
        setError('Failed to fetch admin data: ' + (response.data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('❌ Error fetching admin data:', error);
      setError('Failed to fetch admin data: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  }, []);

  // Refresh admin data
  const handleRefresh = useCallback(async () => {
    try {
      setRefreshing(true);
      setError(null);
      await fetchAdminData();
      setSuccessMessage('Admin data refreshed successfully!');
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (error) {
      console.error('❌ Error refreshing admin data:', error);
      setError('Failed to refresh admin data');
    } finally {
      setRefreshing(false);
    }
  }, [fetchAdminData]);

  // Export admin data as CSV (single file)
  const handleExportAdmins = useCallback(async () => {
    try {
      setExportingAdmins(true);
      setError(null);
      
      console.log('📊 Exporting admin data as CSV...');
      
      const response = await api.get('/api/profiles/admins/export', {
        responseType: 'blob'
      });
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from response headers
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'gbp-admin-data.csv';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('✅ Admin data exported successfully');
      setSuccessMessage('Admin data exported successfully!');
      setTimeout(() => setSuccessMessage(null), 5000);
      
    } catch (error) {
      console.error('❌ Error exporting admin data:', error);
      setError('Failed to export admin data: ' + (error.response?.data?.error || error.message));
    } finally {
      setExportingAdmins(false);
    }
  }, []);

  // Auto-paginated export - creates multiple CSV files
  const handleAutoExportAdmins = useCallback(async () => {
    try {
      setExportingAdmins(true);
      setError(null);
      
      console.log('📊 Starting auto-paginated CSV export...');
      
      const response = await api.get('/api/profiles/admins/export/auto?pageSize=50&maxFiles=20');
      
      if (response.data.success) {
        const { files, summary } = response.data;
        
        console.log(`✅ Auto-export completed: ${summary.totalProcessed} records across ${summary.filesCreated} files`);
        
        // Download each CSV file
        files.forEach((file, index) => {
          if (file.status === 'completed' && file.csvContent) {
            setTimeout(() => {
              const blob = new Blob([file.csvContent], { type: 'text/csv' });
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = file.fileName;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              window.URL.revokeObjectURL(url);
            }, index * 1000); // 1 second delay between downloads
          }
        });
        
        setSuccessMessage(`Auto-export completed! Downloaded ${summary.filesCreated} CSV files with ${summary.totalProcessed} total records.`);
        setTimeout(() => setSuccessMessage(null), 10000);
        
      } else {
        throw new Error(response.data.error || 'Auto-export failed');
      }
      
    } catch (error) {
      console.error('❌ Error in auto-paginated export:', error);
      setError('Failed to complete auto-paginated export: ' + (error.response?.data?.error || error.message));
    } finally {
      setExportingAdmins(false);
    }
  }, []);

  // Open location details
  const handleOpenDetails = useCallback((location) => {
    setSelectedLocation(location);
    setDetailsOpen(true);
  }, []);

  // Close details dialog
  const handleCloseDetails = useCallback(() => {
    setDetailsOpen(false);
    setSelectedLocation(null);
  }, []);

  // Filter admin data based on search term
  const filteredAdminData = useMemo(() => {
    if (!searchTerm) return adminData;
    
    const term = searchTerm.toLowerCase();
    return adminData.filter(location => 
      location.businessName?.toLowerCase().includes(term) ||
      location.address?.toLowerCase().includes(term) ||
      location.phone?.includes(term) ||
      location.website?.toLowerCase().includes(term) ||
      location.admins?.some(admin => 
        admin.name?.toLowerCase().includes(term) ||
        admin.email?.toLowerCase().includes(term)
      )
    );
  }, [adminData, searchTerm]);

  // Calculate statistics
  const stats = useMemo(() => {
    const totalLocations = adminData.length;
    const locationsWithAdmins = adminData.filter(loc => loc.admins && loc.admins.length > 0).length;
    const totalAdmins = adminData.reduce((sum, loc) => sum + (loc.admins?.length || 0), 0);
    const locationsWithoutAdmins = totalLocations - locationsWithAdmins;
    
    return {
      totalLocations,
      locationsWithAdmins,
      totalAdmins,
      locationsWithoutAdmins
    };
  }, [adminData]);

  useEffect(() => {
    fetchAdminData();
  }, [fetchAdminData]);

  if (loading) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ color: 'white', textAlign: 'center', mb: 4 }}>
          Admin Management
        </Typography>
        
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={60} sx={{ color: 'white', mb: 2 }} />
            <Typography variant="h6" sx={{ color: 'white' }}>
              Loading admin data...
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography 
          variant="h4" 
          component="h1" 
          sx={{ 
            fontWeight: 'bold',
            color: '#ffffff',
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
            mb: 2
          }}
          gutterBottom
        >
          Admin Viewer
        </Typography>
        <Typography 
          variant="h6" 
          sx={{ 
            color: 'rgba(255, 255, 255, 0.9)',
            textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
            mb: 4
          }}
        >
          View administrator information for all business locations
        </Typography>
      </Box>

      {/* Action Buttons */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Button
          variant="contained"
          onClick={handleExportAdmins}
          disabled={exportingAdmins}
          startIcon={exportingAdmins ? <Skeleton variant="circular" width={20} height={20} /> : <DownloadIcon />}
          className="modern-button"
          sx={{
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            color: 'white',
            px: 4,
            py: 1.5,
            borderRadius: 2,
            fontWeight: 'bold',
            mr: 2,
            '&:hover': {
              background: 'linear-gradient(135deg, #e085f0 0%, #e54b5b 100%)',
              transform: 'translateY(-2px)',
              boxShadow: '0 8px 25px rgba(240, 147, 251, 0.3)'
            },
            '&:disabled': {
              background: 'rgba(240, 147, 251, 0.5)',
              transform: 'none'
            }
          }}
        >
          {exportingAdmins ? 'Exporting Admins...' : 'Export Admin Data (100 records)'}
        </Button>
        
        <Button
          variant="contained"
          color="secondary"
          onClick={handleAutoExportAdmins}
          disabled={exportingAdmins}
          startIcon={exportingAdmins ? <Skeleton variant="circular" width={20} height={20} /> : <DownloadIcon />}
          className="modern-button"
          sx={{
            ml: 2,
            px: 3,
            py: 1.5,
            borderRadius: 2,
            textTransform: 'none',
            fontSize: '1rem',
            fontWeight: 600,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            '&:hover': {
              boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
              transform: 'translateY(-2px)'
            },
            '&:disabled': {
              opacity: 0.6,
              transform: 'none'
            }
          }}
        >
          {exportingAdmins ? 'Auto-Exporting...' : 'Auto-Export All Data (Multiple Files)'}
        </Button>
        
        <Button
          variant="outlined"
          onClick={testAdminData}
          disabled={loading}
          className="modern-button"
          sx={{
            color: 'white',
            borderColor: 'rgba(255, 255, 255, 0.5)',
            px: 4,
            py: 1.5,
            borderRadius: 2,
            fontWeight: 'bold',
            mr: 2,
            '&:hover': {
              borderColor: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              transform: 'translateY(-2px)',
              boxShadow: '0 8px 25px rgba(255, 255, 255, 0.2)'
            },
            '&:disabled': {
              borderColor: 'rgba(255, 255, 255, 0.3)',
              color: 'rgba(255, 255, 255, 0.5)',
              transform: 'none'
            }
          }}
        >
          Test Admin API
        </Button>
        
        <Button
          variant="outlined"
          onClick={handleRefresh}
          disabled={refreshing}
          startIcon={refreshing ? <Skeleton variant="circular" width={20} height={20} /> : <RefreshIcon />}
          className="modern-button"
          sx={{
            color: 'white',
            borderColor: 'rgba(255, 255, 255, 0.5)',
            px: 4,
            py: 1.5,
            borderRadius: 2,
            fontWeight: 'bold',
            '&:hover': {
              borderColor: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              transform: 'translateY(-2px)',
              boxShadow: '0 8px 25px rgba(255, 255, 255, 0.2)'
            },
            '&:disabled': {
              borderColor: 'rgba(255, 255, 255, 0.3)',
              color: 'rgba(255, 255, 255, 0.5)',
              transform: 'none'
            }
          }}
        >
          {refreshing ? 'Refreshing...' : 'Refresh Data'}
        </Button>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4, justifyContent: 'center' }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card className="modern-card" sx={{ textAlign: 'center', p: 3, height: '100%' }}>
            <Avatar sx={{ bgcolor: 'primary.main', mx: 'auto', mb: 2, width: 56, height: 56 }}>
              <BusinessIcon fontSize="large" />
            </Avatar>
            <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1, color: '#1a202c' }}>
              {stats.totalLocations}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Total Locations
            </Typography>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card className="modern-card" sx={{ textAlign: 'center', p: 3, height: '100%' }}>
            <Avatar sx={{ bgcolor: 'success.main', mx: 'auto', mb: 2, width: 56, height: 56 }}>
              <AdminIcon fontSize="large" />
            </Avatar>
            <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1, color: '#1a202c' }}>
              {stats.locationsWithAdmins}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              With Admins
            </Typography>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card className="modern-card" sx={{ textAlign: 'center', p: 3, height: '100%' }}>
            <Avatar sx={{ bgcolor: 'info.main', mx: 'auto', mb: 2, width: 56, height: 56 }}>
              <PersonIcon fontSize="large" />
            </Avatar>
            <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1, color: '#1a202c' }}>
              {stats.totalAdmins}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Total Admins
            </Typography>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card className="modern-card" sx={{ textAlign: 'center', p: 3, height: '100%' }}>
            <Avatar sx={{ bgcolor: 'warning.main', mx: 'auto', mb: 2, width: 56, height: 56 }}>
              <WarningIcon fontSize="large" />
            </Avatar>
            <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1, color: '#1a202c' }}>
              {stats.locationsWithoutAdmins}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Without Admins
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Search */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
        <TextField
          placeholder="Search locations, admins, or contact info..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: '100%',
            maxWidth: 600,
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              borderRadius: 2,
              '& fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.3)',
              },
              '&:hover fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.5)',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'white',
              },
            },
            '& .MuiInputBase-input': {
              color: '#1a202c',
              '&::placeholder': {
                color: '#4a5568',
                opacity: 1
              }
            }
          }}
        />
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      {/* Success Display */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 4 }}>
          {successMessage}
        </Alert>
      )}

      {/* Admin Data Table */}
      <Card className="modern-card">
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, borderBottom: '1px solid rgba(0, 0, 0, 0.08)' }}>
            <Typography variant="h6" sx={{ color: '#1a202c', fontWeight: 'bold' }}>
              Business Locations & Admins ({filteredAdminData.length})
            </Typography>
          </Box>
          
          {filteredAdminData.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <AdminIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="textSecondary" gutterBottom>
                No admin data found
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {searchTerm 
                  ? 'Try adjusting your search criteria'
                  : 'No admin data has been loaded yet'
                }
              </Typography>
            </Box>
          ) : (
            <TableContainer className="modern-table-container" sx={{ maxHeight: '60vh', overflow: 'auto' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Business Name</TableCell>
                    <TableCell>Location ID</TableCell>
                    <TableCell>Address</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>Admins</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredAdminData.map((location) => (
                    <TableRow key={location.locationId} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                            <BusinessIcon fontSize="small" />
                          </Avatar>
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#1a202c' }}>
                              {location.businessName}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {location.locationId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary" sx={{ maxWidth: 200 }}>
                          {location.address || 'No address'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          {location.phone && (
                            <Typography variant="body2" color="textSecondary">
                              📞 {location.phone}
                            </Typography>
                          )}
                          {location.website && (
                            <Typography variant="body2" color="textSecondary">
                              🌐 {location.website}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {location.admins && location.admins.length > 0 ? (
                            location.admins.map((admin, index) => (
                              <Chip
                                key={index}
                                label={`${admin.name || 'Unknown'} (${admin.email || 'No email'})`}
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ fontSize: '0.75rem' }}
                              />
                            ))
                          ) : (
                            <Chip
                              label="No admins"
                              size="small"
                              color="default"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDetails(location)}
                            sx={{ color: 'primary.main' }}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Location Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">
              Location Details - {selectedLocation?.businessName}
            </Typography>
            <IconButton onClick={handleCloseDetails}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedLocation && (
            <Box>
              {/* Business Information */}
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BusinessIcon color="primary" />
                  Business Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">
                      <strong>Business Name:</strong> {selectedLocation.businessName}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      <strong>Location ID:</strong> {selectedLocation.locationId}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      <strong>GBP ID:</strong> {selectedLocation.gbpId}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">
                      <strong>Account ID:</strong> {selectedLocation.accountId}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      <strong>Phone:</strong> {selectedLocation.phone || 'Not provided'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      <strong>Website:</strong> {selectedLocation.website || 'Not provided'}
                    </Typography>
                  </Grid>
                </Grid>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                  <strong>Address:</strong> {selectedLocation.address || 'Not provided'}
                </Typography>
              </Paper>

              {/* Admin Information */}
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AdminIcon color="primary" />
                  Administrator Information
                </Typography>
                
                {selectedLocation.admins && selectedLocation.admins.length > 0 ? (
                  <List>
                    {selectedLocation.admins.map((admin, index) => (
                      <React.Fragment key={index}>
                        <ListItem>
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: 'primary.main' }}>
                              <PersonIcon />
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                  {admin.name || 'Unknown Name'}
                                </Typography>
                                {admin.role && (
                                  <Chip
                                    label={admin.role}
                                    size="small"
                                    color="secondary"
                                    variant="outlined"
                                  />
                                )}
                              </Box>
                            }
                            secondary={
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="body2" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <EmailIcon fontSize="small" />
                                  {admin.email || 'No email provided'}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                        {index < selectedLocation.admins.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <WarningIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="body1" color="textSecondary">
                      No administrators found for this location
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetails}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminViewer;
